import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../../components/ui/button';
import {
  FileText, Shield, Eye, Download, Lock, ArrowRight,
  Loader2, CheckCircle, Award, FileCheck, Calculator, RefreshCw, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getToken } from '../../services/tokenService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DocProgressBar({ progress, label }) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-400">{label}</span>
        <span className="text-amber-400 font-mono">{progress}%</span>
      </div>
      <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

export default function EtapeDocuments({ souscriptionId, setEtapeCourante, ftpeAccepted, ftpeDejaAdherent = false, ftpeAdhesionId, handleCreateFtpeAdhesion, loading: _parentLoading }) {
  const [_documentsInfo, setDocumentsInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [ftpeCreating, setFtpeCreating] = useState(false);
  const [docReady, setDocReady] = useState({});
  const [generating, setGenerating] = useState({});
  const prefetchedRef = useRef({});

  const authHeaders = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Pré-génération en arrière-plan au montage
  useEffect(() => {
    const prefetch = async () => {
      const token = getToken();
      if (!token || !souscriptionId) return;
      const packs = [
        { key: 'pack1', url: `${API}/souscriptions/${souscriptionId}/download-pack1?draft=true` },
        { key: 'pack2', url: `${API}/souscriptions/${souscriptionId}/download-pack2?draft=true` },
      ];
      for (const pack of packs) {
        if (prefetchedRef.current[pack.key]) continue;
        setGenerating(prev => ({ ...prev, [pack.key]: 10 }));
        try {
          // HEAD request to trigger generation without downloading
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 120000);
          setGenerating(prev => ({ ...prev, [pack.key]: 30 }));
          const res = await fetch(pack.url, {
            method: 'HEAD',
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          });
          clearTimeout(timer);
          if (res.ok) {
            prefetchedRef.current[pack.key] = true;
            setDocReady(prev => ({ ...prev, [pack.key]: true }));
          }
        } catch { /* silently continue */ }
        setGenerating(prev => ({ ...prev, [pack.key]: 100 }));
      }
      setTimeout(() => setGenerating({}), 500);
    };
    prefetch();
  }, [souscriptionId]);

  useEffect(() => {
    const fetchDocumentsInfo = async () => {
      try {
        const res = await axios.get(`${API}/souscriptions-rcd/${souscriptionId}/documents-preview/rcds_unique`, {
          headers: authHeaders()
        });
        setDocumentsInfo(res.data);
      } catch { setDocumentsInfo({ mode: 'lecture_seule', paiement_valide: false }); }
      finally { setLoading(false); }
    };
    fetchDocumentsInfo();
  }, [souscriptionId, authHeaders]);

  const handleAction = async (url, filename, loadingKey, action) => {
    setDownloadLoading(loadingKey);
    try {
      const token = getToken();
      // V38.30 — On utilise `axios` (et non `fetch`) car le Service Worker
      // `sw-push.js` intercepte `fetch` et provoque des erreurs `Response
      // body already used`. axios + responseType=blob contourne cela.
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        validateStatus: () => true,
      });
      if (res.status < 200 || res.status >= 300) {
        // Le blob peut contenir un JSON d'erreur côté backend
        let detail = `HTTP ${res.status}`;
        try {
          const text = await res.data.text();
          if (text) {
            try {
              const j = JSON.parse(text);
              if (j?.detail) detail = j.detail;
            } catch { detail = text.slice(0, 200) || detail; }
          }
        } catch { /* ignore */ }
        throw new Error(detail);
      }
      const blob = res.data;
      const blobUrl = URL.createObjectURL(blob);
      if (action === 'view') {
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
      } else {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
        toast.success('Document telecharge');
      }
    } catch (err) {
      const msg = err?.message ? `Erreur : ${err.message}` : 'Erreur lors du chargement du document';
      toast.error(msg);
    } finally {
      setDownloadLoading(null);
    }
  };

  const handleFtpeCreate = async () => {
    setFtpeCreating(true);
    try { await handleCreateFtpeAdhesion(); } finally { setFtpeCreating(false); }
  };

  const documents = [
    { key: 'pack1', label: 'PACK ATTESTATION', description: 'Attestation de capacite technique et financiere RCDS', filename: `ATTESTATION_RCDS_${souscriptionId}.pdf`, icon: Shield, color: 'blue', url: `${API}/souscriptions/${souscriptionId}/download-pack1?draft=true` },
    { key: 'pack2', label: 'PACK CONDITIONS', description: 'Conditions Particulieres + Conditions Generales', filename: `CONDITIONS_RCDS_${souscriptionId}.pdf`, icon: FileText, color: 'emerald', url: `${API}/souscriptions/${souscriptionId}/download-pack2?draft=true` },
    { key: 'devis', label: 'DEVIS ESS', description: 'Recapitulatif des tarifs et activites selectionnees', filename: `DEVIS_ESS_${souscriptionId}.pdf`, icon: Calculator, color: 'violet', url: `${API}/souscriptions/${souscriptionId}/download-devis?draft=true` },
  ];

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden shadow-xl" data-testid="etape-documents">
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-8 py-5 border-b border-slate-600/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-11 h-11 bg-amber-500/20 rounded-xl flex items-center justify-center"><FileText className="h-5 w-5 text-amber-500" /></div>
            <div><h2 className="text-lg font-bold text-white">Documents de souscription</h2><p className="text-slate-400 text-xs">Consultez et telechargez vos documents</p></div>
          </div>
          {Object.values(docReady).some(Boolean) && (
            <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-medium">
              <Zap className="h-3.5 w-3.5" />Documents prets
            </div>
          )}
        </div>
      </div>

      <div className="p-6 space-y-4">
        {/* Progress bars during generation */}
        {Object.keys(generating).length > 0 && Object.values(generating).some(v => v < 100) && (
          <div className="bg-slate-900/80 border border-amber-500/20 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
              <RefreshCw className="h-4 w-4 animate-spin" />Preparation des documents en cours...
            </div>
            {generating.pack1 !== undefined && generating.pack1 < 100 && <DocProgressBar progress={generating.pack1} label="Attestation" />}
            {generating.pack2 !== undefined && generating.pack2 < 100 && <DocProgressBar progress={generating.pack2} label="Conditions CP/CG" />}
          </div>
        )}

        <div className="space-y-3">
          {documents.map((doc) => {
            const DocIcon = doc.icon;
            const isReady = docReady[doc.key];
            const isLoading = downloadLoading === doc.key || downloadLoading === `${doc.key}_view`;
            return (
              <div key={doc.key} className={`bg-slate-900/50 border rounded-xl p-4 transition-all duration-200 group ${isReady ? 'border-emerald-500/30 hover:border-emerald-400/50' : 'border-slate-700 hover:border-amber-500/50'}`} data-testid={`doc-card-${doc.key}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className={`w-10 h-10 bg-${doc.color}-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                      <DocIcon className={`h-5 w-5 text-${doc.color}-400`} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-white text-sm flex items-center gap-2">
                        {doc.label}
                        {isReady && <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />}
                      </div>
                      <div className="text-xs text-slate-400 truncate">{doc.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => handleAction(doc.url, doc.filename, `${doc.key}_view`, 'view')}
                      disabled={isLoading}
                      variant="outline"
                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 hover:border-blue-400 h-9 px-3 text-xs"
                      data-testid={`doc-view-${doc.key}`}
                    >
                      {downloadLoading === `${doc.key}_view` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5 mr-1.5" />}
                      Voir
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleAction(doc.url, doc.filename, doc.key, 'download')}
                      disabled={isLoading}
                      className="bg-amber-500/90 hover:bg-amber-500 text-white h-9 px-3 text-xs"
                      data-testid={`doc-download-${doc.key}`}
                    >
                      {downloadLoading === doc.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                      Telecharger
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Section FTPE */}
{!ftpeDejaAdherent && (
        <div className="bg-slate-900/50 border border-blue-500/30 rounded-xl overflow-hidden" data-testid="ftpe-adhesion-section">
          <div className="bg-gradient-to-r from-blue-900/50 to-indigo-900/50 px-5 py-3 border-b border-blue-500/20">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 bg-blue-500/20 rounded-lg flex items-center justify-center"><FileCheck className="h-4 w-4 text-blue-400" /></div>
              <div><h3 className="text-sm font-bold text-white">Adhesion partenaire FTPE</h3><p className="text-blue-300/70 text-[10px]">Federation des Tres Petites Entreprises Outremer</p></div>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700"><div className="text-xs text-slate-400">Cotisation annuelle</div><div className="text-xl font-bold text-blue-400 mt-1">200,00 EUR</div></div>
              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700"><div className="text-xs text-slate-400">Paiement</div><div className="text-xs font-medium text-slate-300 mt-1">Direct a la FTPE</div><div className="text-[10px] text-slate-500 mt-1">COPPAM n'encaisse pas cette cotisation</div></div>
            </div>

            {ftpeAccepted ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5"><CheckCircle className="h-4 w-4 text-emerald-400" /><span className="text-emerald-400 font-medium text-xs">Adhesion FTPE creee</span></div>
                {ftpeAdhesionId && (
                  <>
                    <div className="bg-slate-900/50 border border-blue-500/30 rounded-lg p-3 hover:border-blue-400/50 transition-all group" data-testid="ftpe-draft-doc-card">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center space-x-3 min-w-0">
                          <div className="w-9 h-9 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0"><FileCheck className="h-4 w-4 text-blue-400" /></div>
                          <div className="min-w-0"><div className="font-semibold text-white text-xs">BULLETIN ADHESION FTPE</div><div className="text-[10px] text-slate-400">Bulletin d'adhesion FTPE Outremer</div></div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button size="sm" onClick={() => handleAction(`${API}/ftpe-adhesion/workflow/${ftpeAdhesionId}/document?draft=true`, `BULLETIN_FTPE_${ftpeAdhesionId}.pdf`, 'ftpe_view', 'view')} disabled={downloadLoading === 'ftpe_view'} variant="outline" className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 h-8 px-2.5 text-xs" data-testid="ftpe-bulletin-view">
                            {downloadLoading === 'ftpe_view' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                          </Button>
                          <Button size="sm" onClick={() => handleAction(`${API}/ftpe-adhesion/workflow/${ftpeAdhesionId}/document?draft=true`, `BULLETIN_FTPE_${ftpeAdhesionId}.pdf`, 'ftpe_dl', 'download')} disabled={downloadLoading === 'ftpe_dl'} className="bg-amber-500/90 hover:bg-amber-500 text-white h-8 px-2.5 text-xs" data-testid="ftpe-bulletin-download">
                            {downloadLoading === 'ftpe_dl' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-900/50 border border-indigo-500/30 rounded-lg p-3 hover:border-indigo-400/50 transition-all group" data-testid="ftpe-attestation-draft-card">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center space-x-3 min-w-0">
                          <div className="w-9 h-9 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0"><Award className="h-4 w-4 text-indigo-400" /></div>
                          <div className="min-w-0"><div className="font-semibold text-white text-xs">ATTESTATION FTPE</div><div className="text-[10px] text-slate-400">Attestation d'adhesion FTPE</div></div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button size="sm" onClick={() => handleAction(`${API}/ftpe-adhesion/workflow/${ftpeAdhesionId}/attestation-draft`, `ATTESTATION_FTPE_${ftpeAdhesionId}.pdf`, 'ftpe_att_view', 'view')} disabled={downloadLoading === 'ftpe_att_view'} variant="outline" className="border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/10 h-8 px-2.5 text-xs" data-testid="ftpe-attestation-view">
                            {downloadLoading === 'ftpe_att_view' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                          </Button>
                          <Button size="sm" onClick={() => handleAction(`${API}/ftpe-adhesion/workflow/${ftpeAdhesionId}/attestation-draft`, `ATTESTATION_FTPE_${ftpeAdhesionId}.pdf`, 'ftpe_att_dl', 'download')} disabled={downloadLoading === 'ftpe_att_dl'} className="bg-indigo-500/90 hover:bg-indigo-500 text-white h-8 px-2.5 text-xs" data-testid="ftpe-attestation-download">
                            {downloadLoading === 'ftpe_att_dl' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Button onClick={handleFtpeCreate} disabled={ftpeCreating} className="w-full bg-blue-600 hover:bg-blue-500 text-white h-10 text-sm" data-testid="ftpe-create-btn">
                {ftpeCreating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileCheck className="mr-2 h-4 w-4" />}Creer mon adhesion FTPE
              </Button>
            )}
          </div>
        </div>
        )}

        <Button
          onClick={() => {
            // V38.30 — Si une adhésion FTPE existe déjà mais que le parent a
            // perdu ftpeAccepted (refresh, navigation), on autorise quand même
            // d'aller à la signature : le step signature gère lui-même les
            // contrôles.
            if (!ftpeAccepted && !ftpeAdhesionId) {
              toast.error("Créez d'abord votre adhésion FTPE ci-dessus.");
              return;
            }
            setEtapeCourante('signature');
          }}
          disabled={!ftpeAccepted && !ftpeAdhesionId}
          className="w-full h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold text-base shadow-lg shadow-amber-500/20 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="go-to-signature-btn">
          <Lock className="mr-2 h-5 w-5" />Proceder a la signature<ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}
